import React from "react";
import { VideoPlayer } from "./VideoPlayer";
import "./ContentArea.css";
import { ChapterContent, ContentType } from "../Types/CourseTypes";
import { sanitizeChapterHtml } from "../Utils/sanitizeHtml";
import { Document, Page, pdfjs } from "react-pdf";

const API_URL = process.env.REACT_APP_API_URL;

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

interface ContentAreaProps {
  content: ChapterContent | null;
  onContentCompleted?: () => void;
}

export const ContentArea = ({
  content,
  onContentCompleted
}: ContentAreaProps) => {
  if (!content) {
    return (
      <div className="contentAreaRoot">
        <div className="contentAreaMessage">
          Välj ett innehåll i menyn
        </div>
      </div>
    );
  }

  const renderContent = () => {
    const attachment = content.attachment;

    if (attachment?.mimeType?.startsWith("audio/")) {
      return (
        <div className="audioViewerRoot">
          <audio className="audioViewerPlayer" controls preload="metadata" src={attachment.bunnyUrl}>
            Din webbläsare stöder inte ljudspelaren.
          </audio>
        </div>
      );
    }

    if (content.contentType === ContentType.Pdf || attachment?.mimeType === "application/pdf") {
      if (!attachment) {
        return <div className="contentAreaMessage">Fil saknas.</div>;
      }

      return <PdfViewer attachmentId={attachment.id} fileName={attachment.fileName} />;
    }

    switch (content.contentType) {
      case ContentType.Video:
        return (
          <VideoPlayer
            content={content}
            onCompleted={onContentCompleted}
          />
        );

      case ContentType.Attachment:
        if (!attachment) {
          return <div className="contentAreaMessage">Fil saknas.</div>;
        }

        return (
          <div className="attachmentFallbackRoot">
            <div className="attachmentFallbackCard">
              <div className="attachmentFallbackTitle">{attachment.fileName}</div>
              <div className="attachmentFallbackMeta">{attachment.mimeType}</div>
              <a className="attachmentFallbackButton" href={attachment.bunnyUrl} target="_blank" rel="noreferrer">
                Öppna eller ladda ner filen
              </a>
            </div>
          </div>
        );

      case ContentType.Text:
        return (
          <div className="textContentRoot">
            <div
              className="textContentBody"
              dangerouslySetInnerHTML={{
                __html: sanitizeChapterHtml(content.textContent ?? "")
              }}
            />
          </div>
        );

      default:
        return (
          <div className="contentAreaMessage">
            Innehållstyp stöds inte.
          </div>
        );
    }
  };

  return <div className="contentAreaRoot">{renderContent()}</div>;
};

type PdfViewerProps = {
  attachmentId: number;
  fileName: string;
};

const PdfViewer = ({ attachmentId, fileName }: PdfViewerProps) => {
  const [numPages, setNumPages] = React.useState<number | null>(null);
  const [pageWidth, setPageWidth] = React.useState(320);
  const [loadError, setLoadError] = React.useState<string | null>(null);

  const fileUrl = `${API_URL}/api/course-media/attachments/${attachmentId}/proxy`;
  const pdfFile = React.useMemo(() => ({ url: fileUrl }), [fileUrl]);
  const pdfOptions = React.useMemo(() => ({ withCredentials: true }), []);

  React.useEffect(() => {
    const updateWidth = () => {
      const available = Math.max(window.innerWidth - 32, 320);
      setPageWidth(Math.min(available, 1040));
    };

    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  return (
    <div className="pdfViewerRoot">
      <div className="pdfViewerDocument">
        {loadError ? (
          <div className="attachmentFallbackRoot">
            <div className="attachmentFallbackCard">
              <div className="attachmentFallbackTitle">PDF kunde inte visas i spelaren</div>
              <div className="attachmentFallbackMeta">{loadError}</div>
              <a className="attachmentFallbackButton" href={fileUrl} target="_blank" rel="noreferrer">
                Öppna PDF i ny flik
              </a>
              <div className="attachmentFallbackMeta attachmentFallbackMeta--fileName">{fileName}</div>
            </div>
          </div>
        ) : (
          <Document
            file={pdfFile}
            options={pdfOptions}
            loading={<div className="contentAreaMessage">Laddar PDF...</div>}
            error={<div className="contentAreaMessage">Kunde inte ladda PDF-filen.</div>}
            onLoadSuccess={({ numPages: loadedPages }) => setNumPages(loadedPages)}
            onLoadError={(error) => {
              console.error("PDF load error", error);
              setLoadError("PDF.js kunde inte läsa filen. Filen öppnas bäst i ny flik på den här enheten.");
            }}
            onSourceError={(error) => {
              console.error("PDF source error", error);
              setLoadError("Kunde inte hämta filen från CDN.");
            }}
          >
            {Array.from({ length: numPages ?? 0 }, (_, index) => (
              <div key={index + 1} className="pdfViewerPage">
                <Page
                  pageNumber={index + 1}
                  width={pageWidth}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                  onRenderError={(error) => {
                    console.error("PDF render error", error);
                    setLoadError("PDF-sidan kunde inte renderas korrekt i spelaren.");
                  }}
                />
              </div>
            ))}
          </Document>
        )}
      </div>
    </div>
  );
};
